import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const API = `${API_BASE_URL}/api`;

/**
 * Hook to prefetch board data on hover for instant navigation
 * Optimized for speed - no retries, shorter stale time
 */
export const useBoardPrefetch = () => {
  const queryClient = useQueryClient();

  const prefetchBoard = useCallback(async (boardId, workspaceId) => {
    if (!boardId || !workspaceId) return;

    try {
      // Prefetch board data - more aggressive caching
      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: ['board', boardId],
          queryFn: async () => {
            const response = await axios.get(
              `${API}/boards/${boardId}?workspace_id=${workspaceId}`,
              { withCredentials: true }
            );
            return response.data;
          },
          staleTime: 60000, // 1 minute - aggressive caching
        }),
        
        // Prefetch board tasks in parallel
        queryClient.prefetchQuery({
          queryKey: ['tasks', boardId],
          queryFn: async () => {
            const response = await axios.get(
              `${API}/tasks?board_id=${boardId}&workspace_id=${workspaceId}`,
              { withCredentials: true }
            );
            return response.data;
          },
          staleTime: 60000, // 1 minute
        })
      ]);
    } catch (error) {
      // Silently fail - user will get data on actual navigation
      console.debug('Prefetch failed, will load on navigation:', error);
    }
  }, [queryClient]);

  return { prefetchBoard };
};
